import { useDraggable, useElementBounding } from '@vueuse/core';
import { defineComponent, ref, computed, useSlots, watch } from 'vue-demi';
import { s as script$1, O as OrSidebarPlacement } from './OrSidebarCollapseButton-69e7cc73.js';
import { resolveComponent, openBlock, createElementBlock, normalizeClass, renderSlot, createCommentVNode, createElementVNode, normalizeStyle, createBlock, withDirectives, vShow } from 'vue';

const SidebarRoot = [
// Position
'relative',
// Layout
'flex', 'flex-row', 'shrink-0',
// Box
'min-w-0', 'h-full', 'p-0',
// Theme
'bg-background', 'dark:bg-background-dark'];
const SidebarBar = [
// Position
'relative',
// Layout
'flex', 'flex-col', 'shrink-0', 'grow',
// Box
'min-h-0', 'p-0',
// Theme
'text-on-background', 'dark:text-on-background-dark',
// Transition
'transition-[width]'];
const SidebarToolbar = [
// Position
'relative',
// Layout
'flex', 'flex-col', 'shrink-0', 'grow', 'overflow-auto', 'items-center',
// Sizing
'gap-md',
// Box
'min-h-0', 'min-w-xl', 'p-[6px]', 'py-md+',
// Theme
'text-outline', 'dark:text-outline-dark'];
const CollapsibleSidebarToolbar = ['pt-[56px]'];
const SidebarSides = {
  'left': [
  // Borders
  'border-r-[1px]', 'border-r-solid', 'border-r-outline-variant dark:border-r-outline-variant-dark'],
  'right': [
  // Borders
  'border-l-[1px]', 'border-l-solid', 'border-l-outline-variant dark:border-l-outline-variant-dark']
};
const SidebarToolbarSides = {
  'left': [
  // Borders
  'border-r-[1px]', 'border-r-solid', 'border-r-outline-variant dark:border-r-outline-variant-dark'],
  'right': [
  // Borders
  'border-l-[1px]', 'border-l-solid', 'border-l-outline-variant dark:border-l-outline-variant-dark']
};
const SidebarResize = [
// Position
'absolute', 'inset-y-none', 'z-[1]',
// Box
'w-sm+', 'h-[100%]',
// Cursor
'cursor-ew-resize'];
const SidebarResizeSides = {
  'left': [
  // Position
  '-right-[1px]',
  // Borders
  'hover:border-r-2', 'hover:border-r-primary-hover hover:dark:border-r-primary-hover-dark',
  // Transition
  'transition-[border-right]'],
  'right': [
  // Position
  '-left-[1px]',
  // Borders
  'hover:border-l-2', 'hover:border-l-primary-hover hover:dark:border-l-primary-hover-dark',
  // Transition
  'transition-[border-left]']
};
const SidebarCollapseButton = [
// Position
'absolute', 'top-md', 'inset-y-none', 'z-[1]'];
const SidebarCollapseButtonSides = {
  'left': [
  // Position
  '-right-[12px]'],
  'right': [
  // Position
  '-left-[12px]']
};
const SidebarResizingSides = {
  'left': [
  // Borders
  'border-r-2', 'border-r-primary-hover dark:border-r-primary-hover-dark'],
  'right': [
  // Borders
  'border-l-2', 'border-l-primary-hover dark:border-l-primary-hover-dark']
};
const SidebarMain = [
// Layout
'flex', 'flex-col', 'grow', 'overflow-x-hidden',
// Box
'min-h-0'];
const SidebarHeader = [
// Layout
'shrink-0', 'overflow-hidden',
// Sizing
'p-md',
// Typography
'typography-headline-2', 'whitespace-nowrap', 'text-ellipsis'];
const SidebarContent = [
// Layout
'grow', 'overflow-auto',
// Spacing
'p-md'];
const SidebarFooter = [
// Layout
'shrink-0', 'overflow-x-hidden',
// Spacing
'p-md',
// Borders
'border-t-[1px]', 'border-t-solid', 'border-t-outline-variant dark:border-t-outline-variant-dark'];

var script = defineComponent({
  name: 'OrSidebar',
  components: {
    OrSidebarCollapseButton: script$1
  },
  props: {
    width: {
      type: Number,
      default: 320
    },
    minimumWidth: {
      type: Number,
      default: 256
    },
    maximumWidth: {
      type: Number,
      default: 384
    },
    /**
     * User may put the component on the right or the left side of a container.
     * This property mirrors the resizing behavior (drag direction) and styling (horizontal border) of the sidebar.
     *
     * Note: the property doesn't reposition the component in the parent container.
     * @values left, right
     */
    side: {
      type: String,
      default: OrSidebarPlacement.Right
    },
    draggable: {
      type: Boolean,
      default: true
    },
    collapsible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['change:width', 'open', 'close'],
  expose: ['root', 'open', 'close'],
  setup(props, context) {
    // Refs
    const root = ref();
    const resizeBar = ref(null);
    const parentContainer = computed(() => {
      var _a;
      return (_a = root.value) === null || _a === void 0 ? void 0 : _a.parentElement;
    });
    // Slots
    const {
      toolbar: toolbarSlot
    } = useSlots();
    // State
    const startPositionX = ref(0);
    const isResizing = ref(false);
    const isCollapsed = ref(false);
    const maximumWidth = ref(props.maximumWidth || 384);
    const localWidth = ref(props.width >= props.minimumWidth ? props.width : props.minimumWidth);
    const isShowingToolbar = computed(() => !!toolbarSlot);
    // Styles
    const sidebarStyle = computed(() => ({
      width: (isCollapsed.value ? 16 : localWidth.value) + 'px'
    }));
    const rootStyles = computed(() => ['or-sidebar-v3', ...SidebarRoot]);
    const sidebarStyles = computed(() => [...SidebarBar, ...SidebarSides[props.side]]);
    const toolbarStyles = computed(() => [...SidebarToolbar, ...(props.collapsible ? CollapsibleSidebarToolbar : []), ...SidebarToolbarSides[props.side]]);
    const resizeStyles = computed(() => [...SidebarResize, ...SidebarResizeSides[props.side], ...(isResizing.value ? SidebarResizingSides[props.side] : [])]);
    const collapsibleButtonStyles = computed(() => [...SidebarCollapseButton, ...SidebarCollapseButtonSides[props.side]]);
    const mainStyles = computed(() => [...SidebarMain]);
    const headerStyles = computed(() => [...SidebarHeader]);
    const contentStyles = computed(() => [...SidebarContent]);
    const footerStyles = computed(() => [...SidebarFooter]);
    // Effects
    useDraggable(resizeBar, {
      preventDefault: true,
      onStart: (position, event) => {
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
        startPositionX.value = event.x;
        isResizing.value = true;
      },
      onMove: position => {
        const delta = (position.x - startPositionX.value) * (props.side === OrSidebarPlacement.Left ? -1 : 1);
        const newWidth = localWidth.value - delta;
        localWidth.value = newWidth > maximumWidth.value ? maximumWidth.value : newWidth < props.minimumWidth ? props.minimumWidth : newWidth;
        startPositionX.value = position.x;
      },
      onEnd: () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        isResizing.value = false;
      }
    });
    // Methods
    function open() {
      isCollapsed.value = false;
    }
    function close() {
      isCollapsed.value = true;
    }
    const {
      width: containerWidth
    } = useElementBounding(parentContainer);
    watch(() => props.width, newValue => {
      if (newValue !== localWidth.value) {
        localWidth.value = newValue > maximumWidth.value ? maximumWidth.value : newValue < props.minimumWidth ? props.minimumWidth : newValue;
      }
    });
    watch(() => props.minimumWidth, newMinimum => {
      if (newMinimum > localWidth.value) localWidth.value = newMinimum;
    });
    watch(() => props.maximumWidth, newMaximum => {
      if (newMaximum < maximumWidth.value) maximumWidth.value = newMaximum;
    });
    watch(containerWidth, newMaximum => {
      if (newMaximum < maximumWidth.value) maximumWidth.value = newMaximum;
    });
    watch(maximumWidth, newMaximum => {
      if (newMaximum < localWidth.value) localWidth.value = newMaximum;
    });
    watch(localWidth, val => context.emit('change:width', val));
    watch(isCollapsed, val => context.emit(val ? 'close' : 'open'));
    return {
      root,
      resizeBar,
      isResizing,
      isCollapsed,
      localWidth,
      isShowingToolbar,
      sidebarStyle,
      rootStyles,
      sidebarStyles,
      toolbarStyles,
      resizeStyles,
      collapsibleButtonStyles,
      mainStyles,
      headerStyles,
      contentStyles,
      footerStyles,
      open,
      close
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrSidebarCollapseButton = resolveComponent("OrSidebarCollapseButton");
  return openBlock(), createElementBlock("aside", {
    ref: "root",
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.side === 'right' && _ctx.isShowingToolbar ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(_ctx.toolbarStyles)
  }, [renderSlot(_ctx.$slots, "toolbar")], 2 /* CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    class: normalizeClass(_ctx.sidebarStyles),
    style: normalizeStyle(_ctx.sidebarStyle)
  }, [_ctx.draggable && !_ctx.isCollapsed ? (openBlock(), createElementBlock("div", {
    key: 0,
    ref: "resizeBar",
    class: normalizeClass(_ctx.resizeStyles)
  }, null, 2 /* CLASS */)) : createCommentVNode("v-if", true), _ctx.collapsible ? (openBlock(), createBlock(_component_OrSidebarCollapseButton, {
    key: 1,
    modelValue: _ctx.isCollapsed,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.isCollapsed = $event),
    class: normalizeClass(_ctx.collapsibleButtonStyles),
    side: _ctx.side
  }, null, 8 /* PROPS */, ["modelValue", "class", "side"])) : createCommentVNode("v-if", true), withDirectives(createElementVNode("div", {
    class: normalizeClass(_ctx.mainStyles)
  }, [_ctx.$slots.header ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(_ctx.headerStyles)
  }, [renderSlot(_ctx.$slots, "header")], 2 /* CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    class: normalizeClass(_ctx.contentStyles)
  }, [renderSlot(_ctx.$slots, "default")], 2 /* CLASS */)], 2 /* CLASS */), [[vShow, !_ctx.isCollapsed]]), _ctx.$slots.footer ? withDirectives((openBlock(), createElementBlock("div", {
    key: 2,
    class: normalizeClass(_ctx.footerStyles)
  }, [renderSlot(_ctx.$slots, "footer")], 2 /* CLASS */)), [[vShow, !_ctx.isCollapsed]]) : createCommentVNode("v-if", true)], 6 /* CLASS, STYLE */), _ctx.side === 'left' && _ctx.isShowingToolbar ? (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(_ctx.toolbarStyles)
  }, [renderSlot(_ctx.$slots, "toolbar")], 2 /* CLASS */)) : createCommentVNode("v-if", true)], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-sidebar-v3/OrSidebar.vue";

export { script as s };
